import { Alert, Box, Grid } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetMeasureUnitsQuery } from '../../app/redux-fetch/apiCoreQuery'
import { convertToLabelValue } from '../../app/utils'
import DefaultSpacingGrid from '../../components/Containers/DefaultSpacingGrid'
import CombinedSelect from '../../components/Forms/CombinedSelect'
import FormInput from '../../components/Forms/FormInput'
import SharedPhysicalProperties from '../../components/Forms/PhysicalProperties/SharedPhysicalProperties'
import { TableSubTitleTypography } from '../../components/Typographies/TableTitleTypography'
import { IMeasureUnit } from '../Products/types'
import { messages } from './messages'

interface PhysicalPropertiesProps {
    isDisabled?: boolean
}

const PhysicalProperties = ({ isDisabled }: PhysicalPropertiesProps) => {
    const { formatMessage } = useIntl()
    const { watch, formState: { errors } } = useFormContext()

    const { data: measureUnits = [] } = useGetMeasureUnitsQuery()

    const physicalProperties = watch('physicalProperties') ?? {}

    const massUnitOptions = useMemo(() => {
        return convertToLabelValue(
            measureUnits
                .filter((x: IMeasureUnit) => x.measureTypeId === 1)
                .map((x: IMeasureUnit) => {
                    return { name: x.units, id: x.id }
                })
        )
    }, [measureUnits])


    const getUnit = useCallback(
        (id: number) => {
            return measureUnits.find((unit) => unit.id === id)?.units
        },
        [measureUnits]
    )


    const physicalPropertyErrors = (errors?.physicalProperties ?? {}) as Record<string, { message?: string }>

    const validationMessages: string[] = []
    if (physicalPropertyErrors.weightMeasureUnitId?.message) {
        validationMessages.push(physicalPropertyErrors.weightMeasureUnitId.message)
    }
    if (physicalPropertyErrors.lengthMeasureUnitId?.message) {
        validationMessages.push(physicalPropertyErrors.lengthMeasureUnitId.message)
    }
    if (physicalPropertyErrors.volumeMeasureUnitId?.message) {
        validationMessages.push(physicalPropertyErrors.volumeMeasureUnitId.message)
    }
    if (physicalPropertyErrors.kerbWeight?.message) {
        validationMessages.push(physicalPropertyErrors.kerbWeight.message)
    }
    if (physicalPropertyErrors.grossWeight?.message) {
        validationMessages.push(physicalPropertyErrors.grossWeight.message)
    }
    if (physicalPropertyErrors.permissibleLoad?.message) {
        validationMessages.push(physicalPropertyErrors.permissibleLoad.message)
    }

    return (
        <Box sx={{ width: '100%', pb: '88px' }}>
            {validationMessages.length > 0 && (
                <Box sx={{ p: '16px', pb: 0 }}>
                    {validationMessages.map((msg, idx) => (
                        <Alert key={idx} severity='error' sx={{ mb: 1 }}>
                            {msg}
                        </Alert>
                    ))}
                </Box>
            )}
            <Box sx={{ flex: 1, mr: 2, mb: 2, p: '16px' }}>
                <DefaultSpacingGrid container>
                    <Grid item xs={12} md={3}>
                        <CombinedSelect
                            nameValue={'physicalProperties.kerbWeight'}
                            nameUnit={'physicalProperties.weightMeasureUnitId'}
                            labelValue='Kerb Weight'
                            labelUnitValue={formatMessage(messages.uom)}
                            optionsUnit={massUnitOptions}
                            numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                            disabled={isDisabled}
                            required={true}
                            maxValue={99999999.9999}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput
                            name={'physicalProperties.grossWeight'}
                            label='Gross Weight'
                            type='number'
                            unit={getUnit(physicalProperties.weightMeasureUnitId)}
                            required={true}
                            numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                            disabled={isDisabled}
                            maxValue={99999999.9999}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput
                            name={'physicalProperties.permissibleLoad'}
                            label='Permissible Load'
                            type='number'
                            unit={getUnit(physicalProperties.weightMeasureUnitId)}
                            required={true}
                            numberFormatProps={{ decimalScale: 4, fixedDecimalScale: true }}
                            disabled={isDisabled}
                            maxValue={99999999.9999}
                        />
                    </Grid>
                </DefaultSpacingGrid>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                    <TableSubTitleTypography sx={{ ml: 2, mb: 2, fontWeight: 'bold' }}>
                        Internal Dimensions
                    </TableSubTitleTypography>
                    <SharedPhysicalProperties isDisabled={isDisabled} variant='asset' showInternal={true} />
                </Box>
                <Box sx={{ flex: 1, mr: 2 }}>
                    <TableSubTitleTypography sx={{ ml: 2, mb: 2, fontWeight: 'bold' }}>
                        External Dimensions
                    </TableSubTitleTypography>
                    <SharedPhysicalProperties isDisabled={isDisabled} variant='asset' showInternal={false} />
                </Box>
            </Box>
        </Box>
    )
}

export default PhysicalProperties
